// Phase 24A — Mobile Health Report page.
import { todayIST } from "@/lib/istDate";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Info, ShieldOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PresetsMenu from "@/components/reports/PresetsMenu";
import ExportButtons from "@/components/reports/ExportButtons";
import PageHeader from "@/components/PageHeader";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

export default function MobileHealthReport() {
  const today = todayIST();
  const weekAgo = todayIST();   // opens on today
  const [filters, setFilters] = useState({
    date_from: weekAgo, date_to: today,
    zone_id: "", sub_zone_id: "", user_id: "",
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get("/reports/mobile-health", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="mobile-health-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Mobile health" }]}
        title="Mobile health report"
        subtitle="Field-device battery, GPS accuracy and offline-sync statistics."
        favorite={{ key: "reports.mobile-health", type: "screen", label: "Mobile health report", route: "/reports/mobile-health" }}
        testid="mobilehealthreport-page-header"
      />

      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
              Mobile Health Report
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-neutral-400 hover:text-neutral-700 cursor-help"
                            data-testid="mobile-health-info-tooltip">
                      <ShieldOff className="w-4 h-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    IMEI, SIM, signal strength and RAM are not browser-accessible and are
                    intentionally omitted from this report.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h1>
            <p className="text-xs text-neutral-500">One row per user per day (from device_health_daily).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PresetsMenu reportKey="mobile_health" filters={filters}
                          onApply={(f) => setFilters({ ...filters, ...f })} />
          <ExportButtons endpoint="/reports/mobile-health" params={filters} filename="mobile_health" />
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
                    data-testid="refresh-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                              showZone showSubZone showUser showDateRange />
        </CardContent>
      </Card>

      {data?.filter_summary && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> {data.filter_summary} · {total} rows
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs" data-testid="mobile-health-table">
              <thead className="bg-[#6D2C91] text-white sticky top-0">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className="text-left px-3 py-2 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={columns.length || 15}
                            className="text-center p-6 text-neutral-500">
                    No device-health rows for these filters. Field users must sync at least once for data to appear.
                  </td></tr>
                ) : items.map((row, i) => (
                  <tr key={row.user_id + row.date + i} className="border-t hover:bg-neutral-50">
                    {columns.map(c => (
                      <td key={c.key} className="px-3 py-2 whitespace-nowrap">{row[c.key] ?? "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > 50 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span>Page {page} of {Math.ceil(total / 50)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 50 >= total}
                      onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
